// Quick-link (deep-link) definitions for the app selector cards.
//
// Each app's sidebar (defined in `frontend/src/apps/<Module>/components/Layout.tsx`)
// starts with a "Dashboard" entry, which is what clicking the card body already opens.
// To let users deep-link straight into a specific page (e.g. HRM -> Payroll), we surface
// the top three most useful sidebar destinations (after Dashboard) on the right side of
// each card. The route values mirror the `href`s in each app's Layout so the deep link
// lands on exactly the same page the sidebar would.
//
// Keyed by the application *name slug* returned from `GET /api/applications` (e.g. "hrm",
// "crm", "time-camp"). This is the same value used as `requiredApp` in
// `frontend/src/App.tsx` and the value `hasAppAccess` matches case-insensitively.
//
// NOTE: the API response (`Application.toObject()`) has NO `id` field — the Application
// Mongoose model only defines `name`, `displayName`, `route`, `_id`, etc. Earlier iterations
// keyed off `app.id`, which is always `undefined`, so the links never rendered. Always key
// off the `name` slug (use `quickLinksForApp(app)` below to do this safely).

export interface AppQuickLink {
  name: string;
  route: string;
}

const MAX_QUICK_LINKS = 3;

const quickLinksByAppId: Record<string, AppQuickLink[]> = {
  hrm: [
    { name: "Timesheets", route: "/hrm/timesheets" },
    { name: "Leaves", route: "/hrm/leaves" },
    { name: "Payroll", route: "/hrm/payroll/dashboard" },
  ],
  crm: [
    { name: "Contacts", route: "/crm/contacts" },
    { name: "Companies", route: "/crm/companies" },
    { name: "Deals", route: "/crm/deals" },
  ],
  ats: [
    { name: "Job Postings", route: "/ats/jobs" },
    { name: "Candidates", route: "/ats/candidates" },
    { name: "Interviews", route: "/ats/interviews" },
  ],
  finance: [
    { name: "Invoices", route: "/finance/invoices" },
    { name: "Quotations", route: "/finance/quotations" },
    { name: "Payments", route: "/finance/payments" },
  ],
  tasks: [
    { name: "My Tasks", route: "/tasks/my-tasks" },
    { name: "Projects", route: "/tasks/projects" },
    { name: "Labels", route: "/tasks/labels" },
  ],
  assets: [
    { name: "Assets", route: "/assets/inventory" },
    { name: "Assignments", route: "/assets/assignments" },
    { name: "Vendors", route: "/assets/vendors" },
  ],
  documents: [
    { name: "Generate Document", route: "/documents/generate" },
    { name: "My Documents", route: "/documents/list" },
    { name: "Templates", route: "/documents/templates" },
  ],
  automations: [
    { name: "Automations", route: "/automations/list" },
    { name: "Run History", route: "/automations/runs" },
    // NOTE: the Automations app has no `/automations/settings` route (only
    // dashboard, list, new, runs — see apps/Automations/App.tsx), so deep-linking
    // to Settings would dead-end. Use the real "New Automation" route instead.
    { name: "New Automation", route: "/automations/new" },
  ],
  notifications: [
    { name: "Notification Logs", route: "/notifications/logs" },
    { name: "Send Notification", route: "/notifications/send" },
    { name: "Templates", route: "/notifications/templates" },
  ],
  admin: [
    { name: "Users", route: "/admin/users" },
    { name: "Roles & Permissions", route: "/admin/roles-permissions" },
    { name: "Applications", route: "/admin/apps" },
  ],
  lms: [
    { name: "My Courses", route: "/lms/courses" },
    { name: "Enrollments", route: "/lms/enrollments" },
    { name: "Settings", route: "/lms/settings" },
  ],
  "time-camp": [
    { name: "Add Entry", route: "/time-theft/time-entry" },
    { name: "Time Entries", route: "/time-theft/time-entries" },
    { name: "Analysis", route: "/time-theft/analysis" },
  ],
};

/**
 * Returns up to the top three deep-link destinations for a given application name slug.
 * The key is normalized (trimmed + lowercased) so "HRM", "Hrm" and "hrm" all resolve.
 * Returns an empty array for unknown apps so callers can simply render nothing.
 */
export function getAppQuickLinks(appSlug: string | undefined | null): AppQuickLink[] {
  if (!appSlug) return [];
  const key = appSlug.trim().toLowerCase();
  const links = quickLinksByAppId[key];
  if (!links) return [];
  return links.slice(0, MAX_QUICK_LINKS);
}

/**
 * Resolves quick links from a raw application object as returned by `GET /api/applications`.
 * That object has a `name` slug (e.g. "hrm") but NO `id` field, so we key off `name`.
 * This is the selector `AppSelector` uses, and the regression test exercises it directly.
 */
export function quickLinksForApp(app: { name?: string } | undefined | null): AppQuickLink[] {
  if (!app) return [];
  return getAppQuickLinks(app.name);
}

export { quickLinksByAppId, MAX_QUICK_LINKS };
